import type { CSSProperties, ReactNode } from 'react';
import './SegmentedControl.css';

export interface SegmentOption<T extends string = string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
}

export interface SegmentedControlProps<T extends string = string> {
  options: ReadonlyArray<SegmentOption<T> | T>;
  value: T;
  onChange?: (value: T) => void;
  subtle?: boolean;
  style?: CSSProperties;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  subtle = false,
  style,
}: SegmentedControlProps<T>) {
  return (
    <div className={`uc-seg${subtle ? ' uc-seg--subtle' : ''}`} role="tablist" style={style}>
      {options.map((o) => {
        const opt = (typeof o === 'string' ? { value: o, label: o } : o) as SegmentOption<T>;
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            className={`uc-seg__opt${active ? ' uc-seg__opt--active' : ''}`}
            onClick={() => onChange?.(opt.value)}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
